"use client";

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAppDispatch } from "@/redux/hooks";
import {
  setAuth,
  setProfile,
  setAgeRange,
  clearAuthState,
} from "@/redux/slices/auth.Slice";
import { getAgeRangePublicRoute } from "@/helpers/auth/getAgeRange";
import { isBrowser } from "@/helpers/browser.utils";
import { decodeJwtPayload } from "@/helpers/jwt.util";
import {
  AUTH_SYNC_STORAGE_KEY,
  clearAuthTokens,
  getAuthTokens,
  setAuthTokens,
} from "@/helpers/auth/tokenStorage";

// Auth V2 service imports
import { getUser } from "@/services/userService";
import {
  refreshToken as refreshTokenService,
  logout as logoutService,
} from "@/services/authService";

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

// Constants for retry and polling
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // 1 second
const TOKEN_CHECK_INTERVAL = 60000; // Check token every 60 seconds
const SESSION_STORAGE_KEY = 'auth_session_state';

export const AuthProvider = ({ children }) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [authState, setAuthState] = useState({
    isLogin: false,
    user: null,
    token: null,
    activeProfile: null,
  });
  const tokenCheckInterval = useRef(null);
  const isRefreshing = useRef(false);
  const refreshPromise = useRef(null);
  const activeProfileRef = useRef(null);

  // Helper to get tokens from cookies
  const getTokens = useCallback(() => {
    if (!isBrowser()) return { accessToken: null, refreshToken: null };

    return getAuthTokens();
  }, []);

  // Save hydrated user state for fast reloads in the current tab.
  const saveSessionState = useCallback((state) => {
    if (!isBrowser()) return;

    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
        isLogin: state.isLogin,
        user: state.user,
        activeProfile: state.activeProfile,
        timestamp: Date.now(),
      }));
    } catch (error) {
      console.error("Failed to save session state:", error);
    }
  }, []);

  // Load auth state from session storage
  const loadSessionState = useCallback(() => {
    if (!isBrowser()) return null;

    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!stored) return null;

      const data = JSON.parse(stored);
      // Check if session is less than 5 minutes old
      if (Date.now() - data.timestamp < 300000) {
        return data;
      }
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (error) {
      console.error("Failed to load session state:", error);
    }
    return null;
  }, []);

  // Clear all auth data
  const clearAuth = useCallback(({ sync = true } = {}) => {
    activeProfileRef.current = null;
    setAuthState({ isLogin: false, user: null, token: null, activeProfile: null });
    dispatch(clearAuthState());

    if (isBrowser()) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      clearAuthTokens({ sync });
    }
  }, [dispatch]);

  // Refresh token with singleton pattern — uses authService
  const refreshAccessToken = useCallback(async () => {
    // If already refreshing, return the existing promise
    if (isRefreshing.current && refreshPromise.current) {
      return refreshPromise.current;
    }

    isRefreshing.current = true;

    refreshPromise.current = (async () => {
      try {
        const { refreshToken: currentRefreshToken } = getTokens();

        if (!currentRefreshToken) {
          console.warn("No refresh token available — signing out gracefully.");
          clearAuth();
          return null;
        }

        // Use authService.refreshToken
        const data = await refreshTokenService(currentRefreshToken);

        if (data?.accessToken) {
          setAuthState(prev => ({ ...prev, token: data.accessToken }));
          return data.accessToken;
        }

        console.warn("Invalid refresh token response — signing out gracefully.");
        clearAuth();
        return null;
      } catch (error) {
        console.warn("Token refresh failed (handled gracefully):", error?.message || error);
        if ([400, 401, 403, 422].includes(error?.response?.status)) {
          clearAuth();
        }
        return null;
      } finally {
        isRefreshing.current = false;
        refreshPromise.current = null;
      }
    })();

    return refreshPromise.current;
  }, [getTokens, clearAuth]);

  // Fetch user profile with retry logic — uses userService
  const fetchUserProfile = useCallback(async (
    token,
    attempt = 1,
    requestedActiveProfile = null
  ) => {
    try {
      const userData = await getUser();

      if (!userData || !userData.mobileNumber) {
        console.warn("fetchUserProfile: received empty or invalid user data.");
        if (attempt < MAX_RETRY_ATTEMPTS) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * attempt));
          return fetchUserProfile(token, attempt + 1, requestedActiveProfile);
        }
        return null;
      }

      // The interceptor may have rotated the pair while fetching the user.
      const effectiveToken = getTokens().accessToken || token;
      const ageRange = getAgeRangePublicRoute(effectiveToken);
      const tokenPayload = decodeJwtPayload(effectiveToken);
      const tokenProfileId =
        tokenPayload?.profileId ||
        tokenPayload?.profile_id ||
        tokenPayload?.pid;
      const profiles = userData.profiles || [];
      const resolvedActiveProfile =
        requestedActiveProfile ||
        profiles.find((profile) => profile._id === tokenProfileId) ||
        profiles.find(
          (profile) => profile._id === activeProfileRef.current?._id
        ) ||
        null;
      activeProfileRef.current = resolvedActiveProfile;

      // Update Redux state
      dispatch(setProfile({
        isLogin: true,
        profileItems: userData,
        phoneNumber: userData.mobileNumber,
      }));
      dispatch(setAuth(effectiveToken));
      dispatch(setAgeRange(ageRange));

      // Update local state
      const newState = {
        isLogin: true,
        user: userData,
        token: effectiveToken,
        activeProfile: resolvedActiveProfile,
      };
      setAuthState(newState);
      saveSessionState(newState);

      return userData;
    } catch (error) {
      console.warn(`Failed to fetch user profile (attempt ${attempt}, handled gracefully):`, error?.message || error);

      // Handle 401-like responses by refreshing token
      if (error?.response?.status === 401 && attempt === 1) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          return fetchUserProfile(
            newToken,
            attempt + 1,
            requestedActiveProfile
          );
        }
        // Refresh failed — already cleaned up by refreshAccessToken
        return null;
      }

      // Retry logic for network errors
      if (attempt < MAX_RETRY_ATTEMPTS && (!error?.response || error?.response?.status >= 500)) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * attempt));
        return fetchUserProfile(token, attempt + 1, requestedActiveProfile);
      }

      // All retries exhausted — return null, never throw
      return null;
    }
  }, [dispatch, getTokens, refreshAccessToken, saveSessionState]);

  // Initialize auth state
  const initializeAuth = useCallback(async () => {
    setIsLoading(true);

    try {
      const { accessToken } = getTokens();

      if (!accessToken) {
        clearAuth({ sync: false });
        return;
      }

      // Try to load from session first for faster init
      const sessionState = loadSessionState();
      if (sessionState?.isLogin && sessionState?.user) {
        activeProfileRef.current = sessionState.activeProfile || null;
        setAuthState({
          isLogin: true,
          user: sessionState.user,
          token: accessToken,
          activeProfile: sessionState.activeProfile || null,
        });

        // Update Redux immediately
        dispatch(setProfile({
          isLogin: true,
          profileItems: sessionState.user,
          phoneNumber: sessionState.user.mobileNumber,
        }));
        dispatch(setAuth(accessToken));

        // Still fetch fresh data in background
        fetchUserProfile(accessToken).catch(console.error);
      } else {
        // No session state, fetch fresh
        await fetchUserProfile(accessToken);
      }
    } catch (error) {
      console.error("Auth initialization failed:", error);
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  }, [getTokens, clearAuth, fetchUserProfile, loadSessionState, dispatch]);

  // Check token validity periodically — uses userService
  const checkTokenValidity = useCallback(async () => {
    const { accessToken } = getTokens();

    if (!accessToken) {
      clearAuth();
      return;
    }

    try {
      // Make a lightweight API call to check if token is still valid
      await getUser();
    } catch (error) {
      if (error?.response?.status === 401) {
        // Try to refresh
        try {
          await refreshAccessToken();
        } catch (refreshError) {
          clearAuth();
        }
      }
    }
  }, [getTokens, clearAuth, refreshAccessToken]);

  // Listen for storage events (login/logout in other tabs)
  useEffect(() => {
    if (!isBrowser()) return;

    const handleStorageChange = (e) => {
      if (e.key !== AUTH_SYNC_STORAGE_KEY || !e.newValue) return;

      try {
        const change = JSON.parse(e.newValue);
        if (change.type === "logout") {
          clearAuth({ sync: false });
        } else {
          initializeAuth();
        }
      } catch {
        initializeAuth();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [initializeAuth, clearAuth]);

  // Listen for auth failures detected by shared API clients in this tab.
  useEffect(() => {
    if (!isBrowser()) return;

    const handleForcedLogout = () => clearAuth({ sync: false });
    window.addEventListener("auth:logout", handleForcedLogout);
    return () => window.removeEventListener("auth:logout", handleForcedLogout);
  }, [clearAuth]);

  // Listen for visibility changes to refresh auth when tab becomes active
  useEffect(() => {
    if (!isBrowser()) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && authState.isLogin) {
        checkTokenValidity();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [authState.isLogin, checkTokenValidity]);

  // Initialize auth on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Set up periodic token check
  useEffect(() => {
    if (authState.isLogin) {
      tokenCheckInterval.current = setInterval(checkTokenValidity, TOKEN_CHECK_INTERVAL);
    } else {
      if (tokenCheckInterval.current) {
        clearInterval(tokenCheckInterval.current);
        tokenCheckInterval.current = null;
      }
    }

    return () => {
      if (tokenCheckInterval.current) {
        clearInterval(tokenCheckInterval.current);
      }
    };
  }, [authState.isLogin, checkTokenValidity]);

  // Login function
  const login = useCallback(async (
    accessToken,
    refreshTokenVal,
    selectedProfile = null
  ) => {
    try {
      setAuthTokens(accessToken, refreshTokenVal);
      activeProfileRef.current = selectedProfile;
      setAuthState((previous) => ({
        ...previous,
        isLogin: true,
        token: accessToken,
        activeProfile: selectedProfile,
      }));
      await fetchUserProfile(accessToken, 1, selectedProfile);
    } catch (error) {
      // The final session tokens are valid even if the follow-up user request
      // encountered a transient failure. Initialization will retry later.
      console.warn("Failed to hydrate the authenticated user:", error?.message || error);
    }
  }, [fetchUserProfile]);

  // Logout function — uses authService
  const handleLogout = useCallback(async () => {
    try {
      await logoutService();
    } catch (error) {
      console.error("Logout API call failed:", error);
    } finally {
      clearAuth({ sync: false });
    }
  }, [clearAuth]);

  // Set active profile
  const setActiveProfile = useCallback((profile) => {
    activeProfileRef.current = profile;
    setAuthState((previous) => {
      const nextState = { ...previous, activeProfile: profile };
      saveSessionState(nextState);
      return nextState;
    });
  }, [saveSessionState]);

  const refreshUserProfile = useCallback(async () => {
    const { accessToken } = getTokens();
    if (!accessToken) return null;
    return fetchUserProfile(accessToken);
  }, [fetchUserProfile, getTokens]);

  const value = {
    isLoading,
    isAuthenticated: authState.isLogin,
    user: authState.user,
    activeProfile: authState.activeProfile,
    login,
    logout: handleLogout,
    refreshToken: refreshAccessToken,
    checkTokenValidity,
    setActiveProfile,
    refreshUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
